import React, { useEffect, useMemo, useState } from 'react';
import TranslationService from './TranslationService';
import translateDOM from './translateDOM';
import { detectInitialLang, getModulesForRoute, persistLang } from './langUtils';

export const LangContext = React.createContext({
  lang: 'es',
  setLang: null,
  loading: false
});

export default function LangProvider({ children }) {
  const [lang, setLangState] = useState('es');
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const initLang = detectInitialLang();
    const modules = getModulesForRoute(window.location.pathname);
    const ts = TranslationService.getInstance();

    ts.setLang(initLang, modules).then(() => {
      setLangState(initLang);
      setReady(true);
      setTimeout(() => translateDOM(), 0);
    });
  }, []);

  useEffect(() => {
    if (!ready) return undefined;

    let debounceTimer;
    let rafHandle = null;
    let translating = false;

    function hasLangTarget(node) {
      return node.nodeType === 1 && (
        node.hasAttribute('data-lang') ||
        node.hasAttribute('data-lang-attr') ||
        node.hasAttribute('data-lang-html') ||
        node.querySelector('[data-lang], [data-lang-attr], [data-lang-html]')
      );
    }

    function runTranslate() {
      if (translating) return;
      translating = true;
      try {
        translateDOM();
      } finally {
        setTimeout(() => {
          translating = false;
        }, 0);
      }
    }

    function scheduleTranslate() {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (rafHandle) {
          cancelAnimationFrame(rafHandle);
        }

        rafHandle = requestAnimationFrame(() => {
          rafHandle = null;
          runTranslate();
        });
      }, 80);
    }

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const allNodes = [...mutation.addedNodes, ...mutation.removedNodes];
          for (const node of allNodes) {
            if (node.nodeType === 1 && hasLangTarget(node)) {
              scheduleTranslate();
              return;
            }

            const parent = node.parentElement;
            if (parent && hasLangTarget(parent)) {
              scheduleTranslate();
              return;
            }
          }
        }

        if (mutation.type === 'attributes') {
          const target = mutation.target;
          if (hasLangTarget(target)) {
            scheduleTranslate();
            return;
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-lang', 'data-lang-attr', 'data-lang-html', 'data-lang-title']
    });

    return () => {
      clearTimeout(debounceTimer);
      if (rafHandle) {
        cancelAnimationFrame(rafHandle);
      }
      observer.disconnect();
    };
  }, [ready]);

  const setLang = async (newLang) => {
    if (newLang === lang) return;

    setLoading(true);
    const modules = getModulesForRoute(window.location.pathname);
    const ts = TranslationService.getInstance();

    await ts.setLang(newLang, modules);
    persistLang(newLang);
    setLangState(newLang);
    translateDOM();
    setLoading(false);
  };

  const value = useMemo(() => ({ lang, setLang, loading }), [lang, loading]);

  return (
    <LangContext.Provider value={value}>
      {ready ? children : null}
    </LangContext.Provider>
  );
}
